import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { sdk } from "@/lib/medusa";
import {
  loginCustomer,
  logoutCustomer,
  registerCustomer,
  retrieveCustomer,
} from "@/lib/customerAuth";
import { ORDER_TYPE } from "@/lib/orderType";
import {
  refreshCart,
  resetCartsAfterLogout,
} from "@/store/slices/cartSlice";

const initialState = {
  customer: null,
  initialized: false,
  loading: false,
  error: "",
};

async function attachExistingCartsToCustomer(getState, dispatch) {
  const state = getState();
  const cartTypes = [ORDER_TYPE.GROCERY, ORDER_TYPE.TAKEOUT];

  for (const orderType of cartTypes) {
    const cartId = state.cart?.[orderType]?.cart?.id;

    if (!cartId) {
      continue;
    }

    try {
      await sdk.store.cart.transferCart(cartId);
      await dispatch(refreshCart({ orderType, cartId })).unwrap();
    } catch (error) {
      // An empty/already-associated cart should never block customer login.
      console.warn(`Unable to associate ${orderType} cart with customer:`, error);
    }
  }
}

export const initializeCustomer = createAsyncThunk(
  "customer/initialize",
  async (_, { dispatch, getState }) => {
    const customer = await retrieveCustomer();

    if (customer) {
      await attachExistingCartsToCustomer(getState, dispatch);
    }

    return customer;
  },
);

export const loginCustomerAccount = createAsyncThunk(
  "customer/login",
  async ({ email, password }, { dispatch, getState, rejectWithValue }) => {
    try {
      const customer = await loginCustomer({ email, password });

      if (!customer) {
        throw new Error("Unable to retrieve your customer account.");
      }

      await attachExistingCartsToCustomer(getState, dispatch);
      return customer;
    } catch (error) {
      return rejectWithValue(
        error?.message || "Unable to sign in. Check your email and password.",
      );
    }
  },
);

export const registerCustomerAccount = createAsyncThunk(
  "customer/register",
  async (data, { dispatch, getState, rejectWithValue }) => {
    try {
      const customer = await registerCustomer(data);

      if (!customer) {
        throw new Error("Your account was created, but sign in was not completed.");
      }

      await attachExistingCartsToCustomer(getState, dispatch);
      return customer;
    } catch (error) {
      const message = error?.message || "Unable to create your account.";

      if (/already|exists|identity/i.test(message)) {
        return rejectWithValue(
          "An account already exists with this email. Please sign in instead.",
        );
      }

      return rejectWithValue(message);
    }
  },
);

export const logoutCustomerAccount = createAsyncThunk(
  "customer/logout",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await logoutCustomer();
      await dispatch(resetCartsAfterLogout()).unwrap();
      return true;
    } catch (error) {
      return rejectWithValue(error?.message || "Unable to sign out.");
    }
  },
);

const customerSlice = createSlice({
  name: "customer",
  initialState,
  reducers: {
    clearCustomerError(state) {
      state.error = "";
    },
    setCustomer(state, action) {
      state.customer = action.payload || null;
      state.initialized = true;
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(initializeCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.customer = action.payload;
        state.error = "";
      })
      .addCase(initializeCustomer.rejected, (state) => {
        state.loading = false;
        state.initialized = true;
        state.customer = null;
      });

    [loginCustomerAccount, registerCustomerAccount].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = "";
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.loading = false;
          state.initialized = true;
          state.customer = action.payload;
          state.error = "";
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload || action.error.message || "Request failed.";
        });
    });

    builder
      .addCase(logoutCustomerAccount.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(logoutCustomerAccount.fulfilled, (state) => {
        state.loading = false;
        state.customer = null;
        state.initialized = true;
        state.error = "";
      })
      .addCase(logoutCustomerAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || "Unable to sign out.";
      });
  },
});

export const { clearCustomerError, setCustomer } = customerSlice.actions;
export default customerSlice.reducer;
